import { useState, useRef, useCallback } from 'react';

// px of slack that still counts as "at the bottom" (sub-pixel / rounding).
const AT_BOTTOM_SLACK = 40;
// How many layout passes we retry the initial positioning over (the height can
// still be reflowing right after a send).
const INITIAL_SCROLL_ATTEMPTS = 6;
// Marks the user message rows so we can find the last one to pin to the top.
const USER_MSG_SELECTOR = '.chat-user-msg';
// Fallback for releasing the "our own scroll" guard if the smooth scroll never
// reports arriving (e.g. it had nowhere to move).
const PROGRAMMATIC_SETTLE_MS = 500;

/**
 * Chat scroll behaviour, ported from the reference project's chatModal:
 *
 *   • On send, the user's message slides up to the TOP of the viewport and the
 *     reply then streams in below it. A growable spacer at the bottom of the
 *     list gives the message enough room to reach the top even for short replies.
 *   • The reply is NOT auto-followed. We scroll to the user message's own
 *     offsetTop (which never changes as the reply grows below it), so it stays
 *     pinned at the top — it does NOT slide back down once the reply is tall.
 *   • A floating down-arrow appears when the content overflows below the fold;
 *     clicking it smooth-scrolls to the very bottom; scrolling up shows it again.
 *
 * Wire-up (containerRef = the scrollable message container, which must be
 * `position: relative`; mark user rows with the `chat-user-msg` class):
 *
 *   const s = useScrollToBottom(containerRef);
 *   useLayoutEffect(s.syncOnContentChange, [messages, loading, s.syncOnContentChange]);
 *   <Box ref={containerRef} onScroll={s.handleScroll} sx={{ position: 'relative', ... }}>
 *     {messages.map(...)}                       // user rows: className="chat-user-msg"
 *     <div ref={s.bottomSpacerRef} />           // growable spacer (last but one)
 *     <div ref={s.messagesEndRef} />            // scroll anchor (last)
 *   </Box>
 *   // in send handler, before setMessages(userMessage):  s.markUserSent();
 *   <ScrollToBottomButton show={s.showScrollDown} onClick={s.scrollToBottom} />
 */
export default function useScrollToBottom(containerRef) {
  const [showScrollDown, setShowScrollDown] = useState(false);
  const bottomSpacerRef = useRef(null);
  const messagesEndRef = useRef(null);

  const pendingUserScrollRef = useRef(false);  // set on send, consumed on next sync
  const scrollAttemptsLeftRef = useRef(0);     // remaining initial-positioning passes
  const spacerActiveRef = useRef(false);       // spacer currently holding the user msg up
  const programmaticScrollRef = useRef(false); // our own smooth scroll is running
  const programmaticTargetRef = useRef(0);     // scrollTop our smooth scroll is heading to
  const settleTimerRef = useRef(0);
  const rafIdRef = useRef(0);

  const lastUserMessageEl = useCallback(() => {
    const container = containerRef.current;
    if (!container) return null;
    const rows = container.querySelectorAll(USER_MSG_SELECTOR);
    return rows[rows.length - 1] || null;
  }, [containerRef]);

  // Grow/shrink the bottom spacer so the last user message can sit at the top of
  // the viewport (just enough room below it to fill the container).
  //
  // IMPORTANT: we must NOT collapse the spacer to 0 before measuring. The
  // spacer's own height does not affect its offsetTop (that's just the sum of
  // the preceding content), so collapsing is unnecessary — and harmful: on a
  // short conversation, momentarily removing the spacer makes the content too
  // short to hold the scroll position, so the browser CLAMPS scrollTop to 0
  // (the user message jumps back down and the welcome reappears). Measuring with
  // the spacer in place avoids that clamp entirely.
  const sizeSpacer = useCallback(() => {
    const container = containerRef.current;
    const spacer = bottomSpacerRef.current;
    if (!container || !spacer) return;
    const target = lastUserMessageEl();
    if (!target) { spacer.style.height = '0px'; spacerActiveRef.current = false; return; }
    const contentBelowMsg = spacer.offsetTop - target.offsetTop;
    const neededSpacer = Math.max(0, container.clientHeight - contentBelowMsg);
    spacer.style.height = `${neededSpacer}px`;
    spacerActiveRef.current = neededSpacer > 0;
  }, [containerRef, lastUserMessageEl]);

  const updateScrollDownVisibility = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    // Hide while auto-positioning or our own smooth scroll is running.
    if (scrollAttemptsLeftRef.current > 0 || programmaticScrollRef.current) {
      setShowScrollDown(false);
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = container;
    const atBottom = scrollHeight - scrollTop - clientHeight < AT_BOTTOM_SLACK;
    setShowScrollDown(!atBottom && scrollHeight > clientHeight);
  }, [containerRef]);

  // Smooth-scroll the container to a target scrollTop while marking it as "our
  // own" scroll (so its events don't get treated as the user manually scrolling).
  const smoothScrollTo = useCallback((top) => {
    const c = containerRef.current;
    if (!c) return;
    programmaticScrollRef.current = true;
    programmaticTargetRef.current = top;
    c.scrollTo({ top, behavior: 'smooth' });
    // Release the guard once it settles even if no arrival event fires.
    clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      programmaticScrollRef.current = false;
      updateScrollDownVisibility();
    }, PROGRAMMATIC_SETTLE_MS);
  }, [containerRef, updateScrollDownVisibility]);

  const handleScroll = useCallback(() => {
    const c = containerRef.current;
    if (programmaticScrollRef.current) {
      // Release the guard as soon as our smooth scroll reaches its target.
      if (c && Math.abs(c.scrollTop - programmaticTargetRef.current) < 2) {
        programmaticScrollRef.current = false;
        clearTimeout(settleTimerRef.current);
      }
      return;
    }
    // A manual scroll cancels any remaining auto-positioning passes.
    if (scrollAttemptsLeftRef.current > 0) scrollAttemptsLeftRef.current = 0;
    updateScrollDownVisibility();
  }, [containerRef, updateScrollDownVisibility]);

  // Arrow button → jump to the very bottom (latest).
  const scrollToBottom = useCallback(() => {
    const c = containerRef.current;
    if (!c) return;
    smoothScrollTo(c.scrollHeight - c.clientHeight);
  }, [containerRef, smoothScrollTo]);

  // Call right when the user sends a message (before setMessages).
  const markUserSent = useCallback(() => {
    pendingUserScrollRef.current = true;
  }, []);

  // Call from a useLayoutEffect on [messages, loading]. Right after a send it
  // pins the user message to the top (via the spacer); while the reply streams
  // it keeps the spacer sized; otherwise it just refreshes the arrow. It never
  // force-follows the streaming reply.
  const syncOnContentChange = useCallback(() => {
    cancelAnimationFrame(rafIdRef.current);

    if (pendingUserScrollRef.current) {
      pendingUserScrollRef.current = false;
      scrollAttemptsLeftRef.current = INITIAL_SCROLL_ATTEMPTS;
    }

    if (scrollAttemptsLeftRef.current > 0 && containerRef.current && lastUserMessageEl()) {
      scrollAttemptsLeftRef.current -= 1;
      setShowScrollDown(false);
      // 1) Size the spacer now (layout phase, before paint).
      sizeSpacer();
      // 2) After paint, re-measure (layout may have shifted) then smooth-scroll
      //    the user message to the very top. We scroll to the message's own
      //    offsetTop — NOT the absolute bottom: once the reply is taller than the
      //    viewport, "scroll to bottom" slides PAST it (the "slides back down"
      //    bug). Its offsetTop is stable (content above it doesn't change), so it
      //    stays pinned while the reply streams below.
      rafIdRef.current = requestAnimationFrame(() => {
        sizeSpacer();
        const target = lastUserMessageEl();
        if (!target) return;
        smoothScrollTo(target.offsetTop);
      });
      return;
    }

    // Not positioning: keep the spacer sized as the reply grows (never touching
    // scrollTop), then refresh the arrow.
    if (spacerActiveRef.current) sizeSpacer();
    updateScrollDownVisibility();
  }, [containerRef, lastUserMessageEl, sizeSpacer, smoothScrollTo, updateScrollDownVisibility]);

  return {
    showScrollDown,
    bottomSpacerRef,
    messagesEndRef,
    handleScroll,
    scrollToBottom,
    markUserSent,
    syncOnContentChange,
  };
}
